import React, { memo, useContext, useMemo } from 'react'

import { Icon, RotatingSpinner } from '@cgtrader/triangle-ui/dist/src/ssr-index'

// eslint-disable-next-line max-len
import useGetLibraryItems from 'Pods/ThreeDStudio/Components/Sidebar/Components/LibraryContent/Hooks/useGetLibraryItems'
import { VARIATION_TYPES } from 'Pods/ThreeDStudio/constants'
import useGetVariations from 'Pods/ThreeDStudio/Hooks/useGetVariations'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import Card from './Components/Card/Card'
import EmptyState from './Components/EmptyState/EmptyState'
import UploadedModelCard from './Components/UploadedModelCard/UploadedModelCard'
import useVariationActions from './Hooks/useVariationActions'

const spinnerSize = { height: '32px', width: '32px' }

function VariationsTab() {
  const t = useTranslation()
  const pageContext = useContext(ThreeDStudioPageContext)
  const isLoggedIn = pageContext?.userLoggedIn
  const openedModel = useThreeDStudioStore(state => state.ui?.openedModel)
  const isUploadedModelLoaded = useThreeDStudioStore(state => !!state.modelSettings?.inputThreeDUrl)
  const sessionId = openedModel?.generatorSessionId

  const { variations, uploadedAsset, originalRunId, isLoading } = useGetVariations(sessionId)
  const { items: libraryItems } = useGetLibraryItems(isLoggedIn, '')
  const {
    assignMain,
    deleteVariation,
    isAssigning,
    isDeleting,
  } = useVariationActions(variations)

  const mainRunId = useMemo(() => {
    const session = libraryItems.find(item => String(item.id) === String(sessionId))
    const mainRun = session?.attributes?.mainRun
    return mainRun?.id
  }, [libraryItems, sessionId])

  const isEnhanced = useMemo(() =>
    variations.some(variation => VARIATION_TYPES[variation.providerModelId]?.isEnhancement),
    [variations],
  )

  // The original is numberless, like the uploaded-model card, so numbering counts only
  // the variations. Keyed by id rather than derived from the index because the original
  // is not guaranteed to be the oldest run in the list.
  const positionById = useMemo(() => {
    let position = 0
    return new Map(variations.map((variation) => {
      const isOriginal = String(variation.id) === String(originalRunId)
      position += isOriginal ? 0 : 1
      return [String(variation.id), isOriginal ? null : position]
    }))
  }, [variations, originalRunId])

  const isActionLoading = isAssigning || isDeleting

  return (
    <div className="enhance-model-panel__tab">
      <span className="enhance-model-panel__title cgt-heading-50">{t('viewerSidebar.enhance.variations.title')}</span>
      {isLoading && (
        <div className="variations-tab__loading">
          <Icon svgComponent={RotatingSpinner} color="primary" size={spinnerSize} notSpaced />
        </div>
      )}
      {!isLoading && variations.length > 0 && (
        <div className="variations-tab__grid">
          {uploadedAsset && <UploadedModelCard asset={uploadedAsset} isActive={isUploadedModelLoaded} />}
          {variations.map((variation) => (
            <Card
              key={variation.id}
              variation={variation}
              position={positionById.get(String(variation.id))}
              isActive={!isUploadedModelLoaded && String(variation.id) === String(openedModel?.id)}
              isMain={mainRunId != null && String(variation.id) === String(mainRunId)}
              isActionLoading={isActionLoading}
              onSetAsMain={assignMain}
              onDelete={deleteVariation}
            />
          ))}
        </div>
      )}
      {!isLoading && !isEnhanced && <EmptyState />}
    </div>
  )
}

export default memo(VariationsTab)
